/**
 * `BrowserMatchHandle` — the browser-concrete `MatchHandle`,
 * adapting the shared-mode {@link RemoteEncounterController} into `@lancer-vtt/ai`'s
 * transport-agnostic `MatchHandle` seam (`packages/ai/src/matchHandle.ts`), so the shipped
 * `autoplayActivation`/`autoplaySide` policy plays a real live encounter over the SAME
 * long-lived, broadcast-driven connection the browser's own UI already rides — never a second
 * connection, never a re-implementation of the policy. `cli/src/wsMatchHandle.ts` is the sibling
 * (ws-concrete) implementation, and the two differ in one load-bearing way:
 * the CLI opens a **fresh, single-purpose ws round trip per submission** (so "the next message
 * back" is unambiguously the answer to what it just sent), while the browser controller is a
 * **long-lived, shared connection** that also receives OTHER participants' broadcasts — so
 * `submit()` must positively correlate its own submission's settlement, never assume "the next
 * broadcast is mine."
 *
 * ## The correlation problem, and what the wire actually supports
 *
 * Two shapes of `MatchOp` need two DIFFERENT correlation mechanisms, because the wire protocol
 * itself treats them differently:
 *
 *   - **Commands** (`encounter_command` — `end_turn`/`activate_unit`) carry a real per-connection
 *     `seq`, and the server's per-socket **watermark** (echoed on every `*_change`/`*_rejected`
 *     this socket receives) advances uniformly whether the command applied OR was rejected. This
 *     is the TRUE, wire-level correlation — reused here via two
 *     new read-only getters on the base `RemoteController` (`lastSeq`/`watermark`,
 *     `frontend/src/lib/remoteController.ts`), never a re-invented token: `submitCommand` reads
 *     `lastSeq` right after `dispatch()` stamps it, then awaits `watermark >= that seq`.
 *
 *   - **Intents** (`encounter_intent` — `request_move`/`request_attack`) **deliberately carry no
 *     `seq` at all** — confirmed both by `shared/src/index.ts`'s own doc on
 *     `EncounterCommandMessage.seq` ("`encounter_intent` deliberately stays OFF this mechanism —
 *     resolution intents are non-optimistic, so they carry no `seq`") and empirically this
 *     session (a real backend, real ws: an intent's resulting `encounter_change`/
 *     `encounter_command_rejected` echoes the SAME watermark value as before it was sent — the
 *     server's `handle()` only calls `touchWatermark` from the `encounter_command` case,
 *     `backend/src/encounterRoom.ts`). **Making an intent carry a real seq would require the
 *     SERVER to bump this socket's watermark on intent receipt too**, a change inside
 *     `encounterRoom.ts`.
 *
 *     Instead, `submitIntent` correlates via signals the wire ALREADY carries, per-socket-
 *     exclusive or content-addressed, composing the SAME primitives the controller's own
 *     existing UI affordances use for the identical "is this broadcast about MY OWN submission"
 *     question:
 *       1. **Rejection** (`encounter_command_rejected`) is sent to the **originating socket
 *          only** (`backend/src/commandRoom.ts`'s `sendRejection` doc) — ANY fresh rejection
 *          landing on this connection while exactly one submission is outstanding is
 *          unambiguously about that submission. No actor-matching needed; it can't be anyone
 *          else's.
 *       2. **A resolution-bearing intent** (`request_attack`/melee actions) gets a
 *          real `resolution` rider on its `encounter_change`. **Verified against a real server
 *          (`unit-ai-autoplay.e2e.ts`):** `resolution.actorId` is the **issuing slot** (`resolutionAttribution`, `encounterRoom.
 *          ts` — the sender's own bound slot in the ordinary path, e.g. `"gm"`), never the
 *          attacking COMBATANT id `resolutionSource(intent)` returns — a GM driving any number of
 *          GM-owned NPCs always stamps the SAME slot on every one of their resolutions, so the two
 *          could never match except by accident. The wire field that actually names the attacking
 *          combatant is the resolution's own recorded `to_hit`/`miss_chance` step (`ResolvedStep`,
 *          `@lancer-vtt/domain`) — every `resolutionSource`-covered intent type resolves through
 *          one of those ops. `resolutionAttacker` (below) reads it off `resolution.steps` directly
 *          and is what `submitIntent` actually matches against `resolutionSource(intent)`. This is
 *          STRICTLY more correct than actorId-matching would even be if the field carried what its
 *          name implies: two GM-owned combatants attacking concurrently share one slot, so only the
 *          COMBATANT id genuinely discriminates "is this MY attack." A fresh resolution log entry
 *          whose to-hit step names a DIFFERENT attacker (someone else's concurrent action) does NOT
 *          satisfy this — which is the discriminating property this correlation needs.
 *       3. **A move intent carries no resolution rider at all** (`refereeMove`'s plan entries
 *          never set a `broadcastRider` — confirmed by reading `applyRefereePlanEntries`) — its
 *          only observable effect is the plain state change. Correlated by checking the
 *          authoritative state's OWN content: the mover's `pos` now equals the intent's `to`
 *          (`worldFor(state).equals`, the domain's own cell-equality predicate — never a
 *          hand-rolled `x===x && y===y`). An unrelated broadcast that leaves this specific
 *          combatant's position untouched does not satisfy it either.
 *       4. **A fresh prompt** (a different `promptId` than was pending before `submit()`) is
 *          surfaced as an observation exactly like the command path — `autoplayActivation`'s own
 *          halt-on-`prompt` contract is unaffected by which correlation mechanism produced it.
 *
 * All four checks are content/attribution-based, not a synthetic wire token — this mirrors, at
 * the `MatchHandle` layer, the exact pattern the controller's file already uses at the UI layer
 * (`pendingMove_`'s own combatant-id-keyed "is this mine" check, `isMoveHeld`/`useEncounter.ts`).
 *
 * ## The wait has a bounded timeout — every correlation signal above is content/attribution-
 * derived (no wire seq on an intent, per mechanism 2-4), so if a signal is ever missed (a dropped
 * broadcast, a server-side bug, a future widening that emits an unrecognized shape) `waitUntil`
 * would otherwise await forever — a silent, unrecoverable hang on a surface with a human watching.
 * `SUBMIT_TIMEOUT_MS` bounds it: on expiry `submit()` returns
 * a `rejection` observation saying plainly the submission wasn't observably settled in time,
 * rather than hanging — `autoplayActivation`/`autoplaySide` already halt cleanly on any
 * `rejection` (`kind: "rejected"`/`"halted"`), so this is a normal, recoverable outcome, not a new
 * failure mode the callers need to learn. The subscription is always torn down (timeout or
 * settle), never leaked. The budget (15s) is grounded in this exact combat surface's own
 * "generous real round trip + UI settle" ceiling — `frontend/e2e/brace-reaction.e2e.ts` and
 * `frontend/e2e/ammo-preselect.e2e.ts` both use 15000ms for a prompt modal to appear / a
 * resolution to land after a real submission — comfortably above a real server round trip
 * (sub-ms on localhost; ~110ms production RTT) plus the `PresentationDirector`
 * animation settle.
 */
import type { Cell, EncounterCommand, EncounterState } from "@lancer-vtt/domain";
import { activeActor, worldFor } from "@lancer-vtt/domain";
import {
  decidePromptAnswer,
  withRecordedChoice,
  type MatchHandle,
  type MatchObservation,
  type MatchOp,
  type PromptPolicyOutcome,
} from "@lancer-vtt/ai";
import { SEED_OVERLAY, type LoadedOverlay } from "@lancer-vtt/rules";
import { resolutionSource, type EncounterIntent } from "@lancer-vtt/shared";
import { RemoteEncounterController, type BroadcastResolution } from "./remoteEncounterController";

/**
 * The COMBATANT id who attacked in `resolution`, or `null` if its steps carry no `to_hit`/
 * `miss_chance` record (see this file's header) — read directly off the
 * resolution's own recorded steps (`ResolvedStep`, `@lancer-vtt/domain`), never off
 * `resolution.actorId` (the ISSUING SLOT, not a combatant id — confirmed against a real server,
 * not assumed). Every `resolutionSource`-covered intent (`request_attack`/
 * `request_barrage`/`end_attack`) resolves through one of these two ops.
 *
 * **Exported** (`overwatch-brace-interaction-audit`, Task 1) so `CombatApp.tsx`'s Overwatch
 * take-flow ack can discriminate a resolution by its actual attacker rather than by
 * `resolution.actorId` (the issuing/attribution slot — for a GM-controlled NPC watcher whose
 * sibling shot's attribution redirects to the ANSWERING PLAYER, `actorId` never equals the GM's
 * own `encounter.actorId`, so that comparison never fires). Reuse this, don't re-derive the walk.
 */
export function resolutionAttacker(resolution: BroadcastResolution): string | null {
  for (const step of resolution.steps) {
    if (step.op === "to_hit" || step.op === "miss_chance") return step.attacker;
  }
  return null;
}

/** One in-flight `submitIntent` call's snapshot of "what's already happened" — taken right before sending, compared against after. */
type IntentWatermarks = {
  /** {@link RemoteEncounterController.rejection}'s own seq before sending (any HIGHER seq after is a fresh rejection of THIS submission — rejections are per-socket-exclusive, see this file's header). */
  preRejectionSeq: number;
  /** How many entries {@link RemoteEncounterController.resolutions} held before sending (any entry AT OR PAST this index is fresh). */
  preResolutionsLen: number;
  /** {@link RemoteEncounterController.prompt}'s own `promptId` before sending, or `null` (a DIFFERENT non-null promptId after is a fresh prompt). */
  prePromptId: string | null;
  /**
   * `activeActor(state.activation)` before sending — the correlation signal for `request_end_turn`
   * (`silent-equipment-autopick` step 14), which carries neither a resolution rider (unlike an
   * attack) nor a `to`-cell to compare against (unlike a move): the end-of-turn hook chain either
   * fully applies, which genuinely changes who's active (to the next actor, or to `null` between
   * rounds), or halts, which the prompt check above already catches. A settle is "this value
   * changed", never "this value now equals `intent.combatantId`'s *absence*" — comparing to the
   * captured BEFORE value, exactly like every other mark here, is what keeps this honest if
   * `combatantId` was never actually active in the first place (a bug-level submission, per this
   * file's header) rather than reporting a false "settled, no rejection" the instant it's read.
   */
  preActiveActor: string | null;
};

/**
 * How long `waitUntil` will await a correlation signal before giving up (this file's header,
 * "the wait has a bounded timeout"). Exported for tests that want to assert the exact budget;
 * not intended to be tuned per-call — every `submit()` path shares one ceiling.
 */
export const SUBMIT_TIMEOUT_MS = 15_000;

export class BrowserMatchHandle implements MatchHandle {
  private readonly controller: RemoteEncounterController;

  constructor(controller: RemoteEncounterController) {
    this.controller = controller;
  }

  /**
   * The identity this handle's submissions are attributed as — the controller's OWN bound slot
   * id (`*_welcome.you`, roster-slot-identity Decision 0016). Read live off the controller
   * (never cached) so a handle built before the welcome lands still works once it does. Throws
   * if read before the controller is connected — `MatchHandle.actorId` is a non-nullable
   * `string`, so there is no sane empty value to return; every real caller (`autoplayActivation`/
   * `autoplaySide`) only ever reads it after at least one `getState()`/`submit()`, by which point
   * the controller is connected by construction (both round-trip the ws).
   */
  get actorId(): string {
    const id = this.controller.actorId;
    if (id === null) throw new Error("BrowserMatchHandle: not connected (no bound actorId yet)");
    return id;
  }

  async getState(): Promise<EncounterState> {
    return this.currentState();
  }

  async submit(op: MatchOp): Promise<MatchObservation> {
    return op.kind === "command" ? this.submitCommand(op.command) : this.submitIntent(op.intent);
  }

  private currentState(): EncounterState {
    return this.controller.getSnapshot().state;
  }

  /**
   * Resolve `true` once `predicate()` is true — checked immediately, then on every subsequent
   * controller publish (a new `*_change`/`*_rejected`/`*_prompt` message, per
   * {@link RemoteEncounterController.handle}'s own `publish()` calls) — or resolve `false` after
   * `SUBMIT_TIMEOUT_MS` elapses without the predicate ever firing (this file's header, "the wait
   * has a bounded timeout"). The subscription and the timer are torn down on EVERY exit path
   * (settle, timeout) — never left running past whichever happens first.
   */
  private waitUntil(predicate: () => boolean): Promise<boolean> {
    if (predicate()) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (result: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve(result);
      };
      const unsubscribe = this.controller.subscribe(() => {
        if (predicate()) finish(true);
      });
      const timer = setTimeout(() => finish(false), SUBMIT_TIMEOUT_MS);
    });
  }

  /** The `rejection` a `waitUntil` timeout surfaces — plain about what happened, so a caller (and a GM reading the halt) never mistakes it for a legality reject. */
  private timeoutRejection(what: string): { reason: string; category: string } {
    return { reason: `browser match handle: ${what} was not observably settled within ${SUBMIT_TIMEOUT_MS}ms`, category: "timeout" };
  }

  /**
   * A COMMAND submission — the true seq/watermark correlation (this file's header, mechanism 1),
   * EXCEPT for `end_turn` (below). `dispatch()`'s LOCAL reducer check can reject before anything
   * is even sent (a bug-level event in practice, since `autoplayActivation` only ever submits
   * generator-proven-legal plans plus the two hand-built lifecycle commands `end_turn`/
   * `activate_unit`) — surfaced immediately, no wait needed, mirroring `WsMatchHandle`'s own
   * "never infer, always read the field" discipline.
   */
  private async submitCommand(command: EncounterCommand): Promise<MatchObservation> {
    const preRejectionSeq = this.controller.rejection?.seq ?? 0;
    // `end_turn` reroutes onto the `request_end_turn` INTENT one layer down
    // (`RemoteEncounterController.dispatch`'s own override, `decisions/0164` D2) — it can halt
    // server-side for an end-of-turn hazard prompt (System Trauma / the structure-loss
    // interception). `dispatch()` still runs the IDENTICAL local reducer dry-run first (so a
    // bug-level illegal submission is still caught below with nothing sent, exactly as for any
    // other command), but on success it sends an INTENT, not a command — no `seq` is stamped, so
    // this method's ordinary `watermark >= seq` wait would read a stale watermark and report
    // "settled" instantly, and it would never observe a resulting hazard prompt. Capture the
    // intent-style marks BEFORE calling `dispatch` (they must be "before" values) and, on local
    // success, correlate through the SAME classifier `submitIntent` uses for every other
    // halt-capable intent, rather than teaching this method a seq it will never receive.
    const endTurn: { marks: IntentWatermarks; intent: EncounterIntent } | null =
      command.type === "end_turn"
        ? {
            marks: {
              preRejectionSeq,
              preResolutionsLen: this.controller.resolutions.length,
              prePromptId: this.controller.prompt?.promptId ?? null,
              preActiveActor: activeActor(this.currentState().activation),
            },
            intent: { type: "request_end_turn", combatantId: command.combatantId },
          }
        : null;
    const result = this.controller.dispatch(command);
    if (!result.ok) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: { reason: result.error } };
    }
    if (endTurn !== null) {
      const { marks: endTurnMarks, intent: endTurnIntent } = endTurn;
      const settled = await this.waitUntil(() => this.classifyIntentOutcome(endTurnIntent, endTurnMarks) !== null);
      if (!settled) {
        return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: this.timeoutRejection("command (end_turn)") };
      }
      // `settled` is true only once the classifier is non-null — the `!` is sound.
      return this.classifyIntentOutcome(endTurnIntent, endTurnMarks)!;
    }
    const seq = this.controller.lastSeq;
    const settled = await this.waitUntil(() => this.controller.watermark >= seq);
    if (!settled) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: this.timeoutRejection(`command seq ${seq}`) };
    }
    const rejection = this.controller.rejection;
    if (rejection !== null && rejection.seq > preRejectionSeq) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: { reason: rejection.reason, category: rejection.category } };
    }
    return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: null };
  }

  /** An INTENT submission — the content/attribution correlation, this file's header mechanisms 2-4. */
  private async submitIntent(intent: EncounterIntent): Promise<MatchObservation> {
    const marks: IntentWatermarks = {
      preRejectionSeq: this.controller.rejection?.seq ?? 0,
      preResolutionsLen: this.controller.resolutions.length,
      prePromptId: this.controller.prompt?.promptId ?? null,
      preActiveActor: activeActor(this.currentState().activation),
    };
    const sent = this.controller.sendIntent(intent);
    if (!sent) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: { reason: "browser match handle: not connected" } };
    }
    const settled = await this.waitUntil(() => this.classifyIntentOutcome(intent, marks) !== null);
    if (!settled) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: this.timeoutRejection(`intent (${intent.type})`) };
    }
    // `settled` is true only once the classifier is non-null — the `!` is sound.
    return this.classifyIntentOutcome(intent, marks)!;
  }

  /**
   * The one-shot classifier `waitUntil` polls: `null` means "not yet observably settled, keep
   * waiting"; any other value is the final observation. Checked in priority order — a rejection
   * or a fresh prompt short-circuits before either "did it resolve" check runs, mirroring
   * `autoplayActivation`'s own "check `prompt`/`rejection` before reading anything else" contract.
   */
  private classifyIntentOutcome(intent: EncounterIntent, marks: IntentWatermarks): MatchObservation | null {
    const rejection = this.controller.rejection;
    if (rejection !== null && rejection.seq > marks.preRejectionSeq) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: { reason: rejection.reason, category: rejection.category } };
    }
    const prompt = this.controller.prompt;
    if (prompt !== null && prompt.promptId !== marks.prePromptId) {
      return { state: this.currentState(), resolutionSteps: [], prompt, rejection: null };
    }
    const resolutions = this.controller.resolutions;
    if (resolutions.length > marks.preResolutionsLen) {
      const source = resolutionSource(intent);
      // Matched against the resolution's own recorded `to_hit`/`miss_chance` step, NOT
      // `r.actorId` (the issuing SLOT, not a combatant id — see this file's header and
      // `resolutionAttacker` above).
      const fresh = resolutions.slice(marks.preResolutionsLen).find((r) => resolutionAttacker(r) === source);
      if (fresh) {
        return { state: this.currentState(), resolutionSteps: fresh.steps, prompt: null, rejection: null };
      }
      // Fresh resolutions arrived, but none whose to-hit step names OUR intent's source as the
      // attacker — someone else's concurrent action. Deliberately falls through to keep waiting
      // rather than resolving on an unrelated broadcast (the discriminating property this file's
      // header calls out).
    }
    // `request_move` never gets a resolution rider (`refereeMove`'s plan entries carry none) —
    // its only observable effect is the state itself. `request_end_turn` (below) is the SECOND
    // such case (`silent-equipment-autopick` step 14): `applyHookedPlanTail` gives it the same
    // COMMS feed line a raw `end_turn` command gets, never a `resolution` rider. Every other
    // intent this package's generator ever emits (`request_attack`, the melee-action synthetics —
    // Ram/Grapple/Improvised Attack, still wire-shaped `request_attack`) IS resolution-bearing and
    // was already handled above; a future widening that adds a non-resolution-bearing intent past
    // these two would need its own case here.
    if (intent.type === "request_move") {
      const state = this.currentState();
      const pos = state.combatants[intent.combatantId]?.pos;
      if (pos !== undefined && pos !== null && worldFor(state).equals(pos, intent.to as Cell)) {
        return { state, resolutionSteps: [], prompt: null, rejection: null };
      }
    }
    // `request_end_turn` carries no resolution and no `to`-cell to compare — the observable
    // effect of a clean (non-halting) end turn is that `combatantId` genuinely stops being the
    // active actor (advances to the next actor, or to `null` between rounds). Compared against
    // the BEFORE value captured in `marks`, never against `intent.combatantId`'s absence directly
    // — see {@link IntentWatermarks.preActiveActor}'s doc for why that distinction matters.
    if (intent.type === "request_end_turn" && activeActor(this.currentState().activation) !== marks.preActiveActor) {
      return { state: this.currentState(), resolutionSteps: [], prompt: null, rejection: null };
    }
    return null;
  }
}

/**
 * Whether `prompt`'s decider is controlled by the driving actor `actorId` — the SAME gating
 * condition `CombatApp.tsx`'s `suppressPromptModalForGmBystander` already uses to decide "is
 * this GM viewer the actual decider, or just a bystander backstop recipient", read here off the
 * wire field directly rather than importing a route
 * component. True when `whoSlotIds` is absent (the decider has no controlling slot — a bare NPC,
 * GM-controlled by default) or equals `actorId` (the driving GM literally owns the decider,
 * e.g. an NPC-side Brace). False for a specific OTHER human's decision.
 */
export function isOwnControlledPrompt(prompt: { whoSlotIds?: string[] }, actorId: string): boolean {
  return prompt.whoSlotIds === undefined || prompt.whoSlotIds.includes(actorId);
}

/**
 * The browser's narrower prompt policy — a DELIBERATE divergence from the CLI's answer-everything
 * GM backstop; don't "fix" it to match. Composes `decidePromptAnswer`/`withRecordedChoice`
 * (`@lancer-vtt/ai`) for the
 * classification and the recorded-answer re-submit shape — never re-derives either — but
 * reimplements `answerPrompt`'s OWN driving loop rather than delegating to it outright, because
 * `answerPrompt`'s loop does not re-check ownership per iteration: a checkpoint-take's sibling
 * attack, or a second watcher's own offer, can belong to a DIFFERENT decider than the one that
 * raised the first prompt in the chain, and each must be gated independently. Same shape as
 * `packages/ai/src/promptPolicy.ts`'s `answerPrompt` otherwise (the interleaving contract for a
 * checkpoint "take": the sibling attack resolves to completion before the recorded answer
 * re-submits) — matches `autoplay.ts`'s exported `PromptPolicy` signature, so it drops straight
 * into `autoplayActivation`/`autoplaySide`'s `promptPolicy` arg.
 */
export async function browserPromptPolicy(args: {
  handle: MatchHandle;
  observation: MatchObservation;
  overlay?: LoadedOverlay;
}): Promise<PromptPolicyOutcome> {
  const { handle, overlay = SEED_OVERLAY } = args;
  let obs = args.observation;
  while (obs.prompt) {
    const prompt = obs.prompt;
    if (!isOwnControlledPrompt(prompt, handle.actorId)) return { kind: "unhandled", prompt };
    const decision = decidePromptAnswer({ prompt, state: obs.state, overlay });
    if (decision.kind === "unhandled") return { kind: "unhandled", prompt };

    if (decision.kind === "checkpoint-take") {
      const siblingObs = await handle.submit({ kind: "intent", intent: decision.watcherAttack });
      if (siblingObs.rejection) return { kind: "unhandled", prompt };
      if (siblingObs.prompt) {
        // Recurse through THIS gated policy, never the shipped `answerPrompt` — the sibling's
        // own prompt (e.g. the mover's Brace in response to the watcher's shot) needs the SAME
        // ownership check, not an unconditional answer.
        const resolvedSibling = await browserPromptPolicy({ handle, observation: siblingObs, overlay });
        if (resolvedSibling.kind === "unhandled") return resolvedSibling;
      }
    }

    const answeredIntent = withRecordedChoice(prompt.intent, prompt.promptId, decision.optionId);
    obs = await handle.submit({ kind: "intent", intent: answeredIntent });
  }
  return { kind: "resolved", observation: obs };
}
